import React from 'react'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { LKDate } from 'loankeeper-common/helpers/Date'
import { date as dateFormatter } from 'loankeeper-common/helpers/formatters'
// @ts-ignore
import LKModal, { actions as LKModalActions } from 'shared/LKModal'
import ReminderModal, { MODAL_NAME as REMINDER_MODAL } from './Info/Notes/Reminders/Modal/ReminderModal'
import { colors, usePermissions, useUserConfig } from 'shared'
import { useDispatch, useSelector } from 'react-redux'
import { translateActiveNotes as translate, translateWebMessages, translateLKMessage } from './translator'
import {
  ActiveNotesModal as SharedActiveNotesModal,
  openActiveNotesModal as openNotesModal,
} from '../shared/ActiveNotesChecker'
import { openFileDetails } from '../FileDetails'
import ClientReminderModals from '../ClientDetails/Tabs/Notes/Modals'
import { openAddNoteModal, openDeleteNoteModal } from '../ClientDetails/Tabs/Notes/Modals'
import { modalActionTypes } from '../ClientDetails/constants'
import { ActiveNoteContextMenuActions, ActiveNoteData } from './constants'
import { deleteReminder, formatObjectToCommon, getClientData, getLoanData } from './note-helpers'
import { selectors as DCSelectors, actions as DCActions } from '../DetailsContainer'

const MODAL_NAME = 'LoanDetailsActiveNotesModal'
const REMINDER_DELETE_MODAL = 'ReminderDeleteModal'
export const openActiveNotesModal = () => openNotesModal(MODAL_NAME)

const getConfig = (actions, loanFiles, clientFiles, permissions, userId) => ({
  instanceName: 'loan-details-active-notes',
  gridIdentity: { formName: 'frm_RMD_All', objectName: 'dbgrd_reminders' },
  enableColumnsResize: true,
  enableColumnsOrder: true,
  enableColumnsHide: true,
  enableSorting: false,
  enableTranslate: true,
  enableRowSelection: true,
  syncGridState: false,
  columnDefs: [
    { headerName: 'NT', field: 'NT', width: 30 },
    { headerName: 'CDate', field: 'CDate' },
    { headerName: 'LoginFull', field: 'LoginFull' },
    { headerName: 'Type', field: 'Type' },
    { headerName: 'Body', field: 'Body' },
    { headerName: 'GDate', field: 'GDate' },
  ],
  columnConfigs: {
    NT: {
      iconComponent: (value) => (value === 'CL' ? 'client' : 'loan'),
    },
    CDate: {
      comparator: (a, b) => LKDate.compareSameFormat(a, b, 'YYYY-MM-DD HH:mm:ss'),
      valueFormatter: ({ value }) => new LKDate(value).toTString(),
    },
    GDate: {
      comparator: (a, b) => LKDate.compareSameFormat(a, b, 'YYYY-MM-DD HH:mm:ss'),
      valueFormatter: ({ value }) => dateFormatter(value),
    },
  },
  getRowStyle: ({
    node: {
      data: { PriorityID },
    },
  }) => {
    const styles = {} as any

    if (PriorityID === 2) {
      styles.color = colors.red
    }

    if (PriorityID === 3) {
      styles.color = colors.ink
    }

    return styles
  },
  getContextMenuItems: ({ node: { data } }) => {
    const noteType = Object.hasOwnProperty.call(data, 'LoanID') ? 'loan' : 'client'
    const files = noteType === 'loan' ? loanFiles : clientFiles
    const { LFLID } = data
    let file

    if (files?.length) {
      file = files.find((x) => x.ID === LFLID && ['jpg', 'pdf'].includes(x.Ext.toLowerCase()))
    }

    return [
      {
        name: translateWebMessages('cmd_preview_linkedfile'),
        action: () => actions[noteType].openPreviewNoteModal(data),
        icon: 'preview',
      },
      ...(noteType === 'client' || userId === data.UserID || permissions.EDIT_RMD_ALIEN
        ? [
            {
              name: translate('cmd_edit'),
              icon: 'edit',
              action: () => actions[noteType].openEditNoteModal(data),
              permissionKey: 'EDIT_RMD',
            },
          ]
        : []),
      ...(noteType === 'client' || userId === data.UserID || permissions.DEL_RMD_ALIEN
        ? [
            {
              name: translate('cmd_delete'),
              icon: 'delete',
              action: () => actions[noteType].openNoteDelete(data),
              permissionKey: 'DEL_RMD',
            },
          ]
        : []),

      ...(file
        ? [
            {
              name: translate('cmd_view_lfl'),
              action: () => {
                actions.openFileDetails({
                  ID: LFLID,
                  hash: file.HashSum_FileID_,
                  type: file.Ext || 'jpg',
                })
              },
              icon: 'preview',
            },
          ]
        : []),
    ]
  },
})

interface ModalParams {
  onClose?: Function
  data: any
}

const ActiveNotesModal = ({ onClose, data }: ModalParams) => {
  const dispatch = useDispatch()
  const permissions = usePermissions()
  const { UserID } = useUserConfig('ID as UserID')

  const dataRef = React.useRef<ActiveNoteData>()
  const prevData = dataRef.current

  const [note, setNote] = React.useState()
  const [noteData] = React.useState(formatObjectToCommon(data))
  const [activeNotes, setActiveNotes] = React.useState([])
  const [clientFiles, setClientFiles] = React.useState([])
  const [fetching, setFetching] = React.useState(false)

  const loanFilesFetchState = useSelector((state) =>
    DCSelectors.dataExistsOrIsBeingFetched(state, { type: 'Loan', ID: noteData.id, page: 'linkedfiles' }),
  )
  const [loanFiles, setloanFiles] = React.useState(isEmpty(loanFilesFetchState) ? [] : loanFilesFetchState)
  const loanRemindersFetchState = useSelector((state) =>
    DCSelectors.dataExistsOrIsBeingFetched(state, { type: 'Loan', ID: noteData.id, page: 'reminders' }),
  )

  React.useEffect(() => {
    if (!isEqual(prevData, data)) {
      loadData()
    }
    dataRef.current = data
  }, [data])

  React.useEffect(() => {
    if (loanFilesFetchState === true) {
      setFetching(true)
    }

    if (loanFilesFetchState?.length && !loanFiles?.length) {
      setloanFiles(loanFilesFetchState)
      setFetching(false)
    }
  }, [loanFilesFetchState, loanFiles])

  React.useEffect(() => {
    if (loanRemindersFetchState?.length) {
      setActiveNotes(loanRemindersFetchState)
    } else if (loanRemindersFetchState === true) {
      setFetching(true)
    }
  }, [loanRemindersFetchState])

  const loadData = React.useCallback(async () => {
    try {
      setFetching(true)
      const { clientNotes, clientFiles } = await getClientData(noteData.clientid, noteData.clienthash, dispatch)
      const { loanNotes, loanFiles } = await getLoanData(noteData.id, noteData.hash, dispatch)

      setActiveNotes([...clientNotes, ...loanNotes])
      setClientFiles(clientFiles)

      if (!loanFilesFetchState?.length && loanFiles?.length) {
        setloanFiles(loanFiles)
      }

      setFetching(false)
    } catch (error) {
      console.warn(error)
      setFetching(false)
    }
  }, [setActiveNotes])

  const refresh = React.useCallback(async () => {
    setActiveNotes([])
    loadData()
  }, [setActiveNotes, noteData.id, noteData.hash])

  const handleRemindersRefresh = React.useCallback(() => {
    dispatch(
      DCActions.fetchPage({
        type: 'Loan',
        ID: noteData.id,
        hash: noteData.hash,
        page: 'reminders',
      }),
    )
    loadData()
  }, [noteData.id, noteData.hash, loadData])

  const contextMenuActions = React.useMemo(
    () => ({
      client: {
        openEditNoteModal: (data) => {
          setNote(data)
          return dispatch(openAddNoteModal(modalActionTypes.EDIT))
        },
        openPreviewNoteModal: (data) => {
          setNote(data)
          return dispatch(openAddNoteModal(modalActionTypes.PREVIEW))
        },
        openNoteDelete: (params) => dispatch(openDeleteNoteModal(data.ClientID, data.HashSum_ClientID_, params.ID)),
      } as ActiveNoteContextMenuActions,
      loan: {
        openEditNoteModal: (data) =>
          dispatch(
            LKModalActions.open({
              name: REMINDER_MODAL,
              data: {
                ...data,
                Comment: data.Body,
                GDate: new LKDate(data.GDate).toString(),
                StateID: data.StateID === 1,
              },
            }),
          ),
        openPreviewNoteModal: function (data) {
          return this.openEditNoteModal({ ...data, __readonly: true })
        },
        openNoteDelete: (data) =>
          dispatch(
            LKModalActions.openConfirmation(REMINDER_DELETE_MODAL, {
              message: translateLKMessage('CONFIRM_REMINDER_DELETE'),
              ...data,
            }),
          ),
      } as ActiveNoteContextMenuActions,
      openFileDetails: (params) => dispatch(openFileDetails(params)),
    }),
    [dispatch],
  )

  const gridConfig = React.useMemo(
    () => getConfig(contextMenuActions, loanFiles, clientFiles, permissions, UserID),
    [contextMenuActions, loanFiles, clientFiles, permissions, UserID],
  )

  const handleDeleteReminder = async (data) => {
    await deleteReminder(data.ID, noteData.id, noteData.hash, dispatch)
    refresh()
  }
  return (
    <>
      {noteData && (
        <>
          <ReminderModal
            ID={noteData.id}
            hash={noteData.hash}
            productType={noteData?.producttype}
            linkedfiles={loanFiles}
            onRefresh={handleRemindersRefresh}
          />

          <LKModal.Confirmation name={REMINDER_DELETE_MODAL} onConfirm={handleDeleteReminder} />

          <ClientReminderModals
            ID={noteData.clientid}
            hash={noteData.clienthash}
            linkedfiles={clientFiles}
            selected={note}
            onRefresh={refresh}
          />
        </>
      )}

      <SharedActiveNotesModal
        name={MODAL_NAME}
        isFetching={fetching}
        heading={translate('frm_RMD_All')}
        onRefresh={refresh}
        onClose={onClose}
        title={translate('pan_loan')}
        notes={activeNotes}
        gridConfig={gridConfig}
      />
    </>
  )
}

export default ActiveNotesModal
